import { useCallback, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  cancelChartAlert, chartAlertNotification, createChartAlert, formatChartAlertLevel,
  getChartAlerts, updateChartAlert,
  type ChartAlertDelivery, type ChartAlertDraft, type ChartAlertNotification, type ChartAlertsResponse,
} from '@/lib/chartAlerts'
import { toast } from '@/hooks/use-toast'

function defaultDelivery(username: string | undefined): ChartAlertDelivery {
  if (!username) return 'telegram'
  try {
    const saved = localStorage.getItem(`personal-chart-alert-delivery:${username}`)
    return saved === 'pushover' || saved === 'emergency' ? saved : 'telegram'
  } catch { return 'telegram' }
}

export function useChartAlerts(username: string | undefined, symbol: string | null) {
  const client = useQueryClient()
  const key = ['personal-chart-alerts', username]
  const [selection, setSelection] = useState<{ username: string | undefined; delivery: ChartAlertDelivery }>(() => ({ username, delivery: defaultDelivery(username) }))
  const delivery = selection.username === username ? selection.delivery : defaultDelivery(username)
  const creating = useRef(false)
  const owner = useRef(username)
  owner.current = username
  const query = useQuery({
    queryKey: key, queryFn: getChartAlerts, enabled: !!username,
    refetchInterval: 5000, retry: false,
  })
  const changed = () => client.invalidateQueries({ queryKey: key })
  const mutation = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch?: Partial<ChartAlertNotification> & { threshold?: number } }) =>
      patch ? updateChartAlert(id, patch) : cancelChartAlert(id),
    onSuccess: async ({ alert }) => {
      await client.cancelQueries({ queryKey: key })
      client.setQueryData<ChartAlertsResponse>(key, (previous) => previous && {
        ...previous, alerts: previous.alerts.map((item) => item.id === alert.id ? alert : item),
      })
      await changed()
    },
  })
  const creation = useMutation({ mutationFn: createChartAlert, onSuccess: changed })
  const create = useCallback(async (draft: ChartAlertDraft) => {
    if (!username || !symbol || creating.current) return
    const channel = delivery === 'telegram' ? 'telegram' : 'pushover'
    const availability = query.data?.delivery_options[channel]
    if (!availability?.ready) {
      toast({ title: 'Alert not created', description: availability?.error || 'Open My alerts to check your notification connection.', variant: 'destructive' })
      return
    }
    creating.current = true
    try {
      const { alert } = await creation.mutateAsync({ ...draft, symbol, ...chartAlertNotification(delivery) })
      if (owner.current === username) toast({
        title: 'Personal alert enabled',
        description: `${alert.symbol} · ${alert.exchange.toUpperCase()} ${alert.metric === 'price' ? 'price' : 'OI'} ${alert.direction === 'above' ? '≥' : '≤'} ${formatChartAlertLevel(alert)}. Manage delivery in My alerts.`,
      })
    } catch (error) {
      if (owner.current === username) toast({ title: 'Alert not created', description: error instanceof Error ? error.message : 'Please try again.', variant: 'destructive' })
    } finally {
      creating.current = false
    }
  }, [username, symbol, delivery, query.data, creation.mutateAsync])
  const moveAlert = useCallback(async (id: string, threshold: number) => {
    try {
      const result = await mutation.mutateAsync({ id, patch: { threshold } })
      if (owner.current === username) toast({ title: 'Alert value updated', description: `${result.alert.symbol} · ${result.alert.exchange.toUpperCase()} ${formatChartAlertLevel(result.alert)}` })
      return result
    } catch (error) {
      if (owner.current === username) toast({ title: 'Alert not updated', description: error instanceof Error ? error.message : 'Please try again.', variant: 'destructive' })
      throw error
    }
  }, [username, mutation.mutateAsync])
  const deleteAlert = useCallback(async (id: string) => {
    try {
      const result = await mutation.mutateAsync({ id })
      if (owner.current === username) toast({ title: 'Alert deleted', description: `${result.alert.symbol} · ${result.alert.exchange.toUpperCase()} ${formatChartAlertLevel(result.alert)}` })
      return result
    } catch (error) {
      if (owner.current === username) toast({ title: 'Alert not deleted', description: error instanceof Error ? error.message : 'Please try again.', variant: 'destructive' })
      throw error
    }
  }, [username, mutation.mutateAsync])
  const controls = useMemo(() => ({
    alerts: (query.data?.alerts || []).filter((alert) => alert.symbol === symbol && alert.status === 'active'),
    onCreate: (draft: ChartAlertDraft) => { void create(draft) },
    onUpdate: moveAlert,
    onDelete: deleteAlert,
    isSaving: mutation.isPending,
  }), [query.data, symbol, create, moveAlert, deleteAlert, mutation.isPending])

  return {
    alerts: query.data?.alerts || [],
    availability: query.data?.delivery_options,
    isLoading: query.isLoading,
    error: query.error,
    controls,
    delivery,
    setDelivery: (next: ChartAlertDelivery) => {
      setSelection({ username, delivery: next })
      if (username) {
        try { localStorage.setItem(`personal-chart-alert-delivery:${username}`, next) } catch { /* The current selection still works without browser storage. */ }
      }
    },
    isSaving: mutation.isPending,
    savingId: mutation.isPending ? mutation.variables?.id : undefined,
    update: (id: string, patch: Partial<ChartAlertNotification> & { threshold?: number }) => mutation.mutateAsync({ id, patch }),
    cancel: (id: string) => mutation.mutateAsync({ id }),
    refresh: () => { void query.refetch() },
  }
}

export type PersonalChartAlerts = ReturnType<typeof useChartAlerts>
